import {isNil} from 'ramda';

import type {OnlineOfferEngagementResponseBody} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {buildArray, type Nullish} from 'shared/web';

import {ENGAGEMENT_SEGMENT_SEPARATOR} from '../constants/ENGAGEMENT_SEGMENT_SEPARATOR';
import {formatEngagementActiveTime} from './formatEngagementActiveTime';
import {formatEngagementLastViewed} from './formatEngagementLastViewed';
import {getEngagementScrollSegment} from './getEngagementScrollSegment';

const ONLINE_OFFER_ENGAGEMENT_KEYS = {
  openedCount: 'page.businessCase.labels.onlineOfferEngagement.openedCount',
  lastViewed: 'page.businessCase.labels.onlineOfferEngagement.lastViewed',
  totalActiveTime: 'page.businessCase.labels.onlineOfferEngagement.totalActiveTime',
  certificateViewed: 'page.businessCase.labels.onlineOfferEngagement.certificateViewed',
} as const;

/**
 * Builds the single-line engagement summary shown on the shared-offer alert.
 * Returns null when the customer has never opened the offer, so callers can
 * omit the line entirely.
 */
export const getOnlineOfferEngagementSummary = (
  engagement: OnlineOfferEngagementResponseBody | Nullish
) => {
  if (isNil(engagement) || engagement.openCount === 0) {
    return null;
  }

  const lastViewedSegment = getLastViewedSegment(engagement.lastOpenedAt);
  const totalActiveTimeSegment = getTotalActiveTimeSegment(engagement.activeTimeMs);
  const scrollSegment = getEngagementScrollSegment(engagement.maxScrollDepth);

  return buildArray<string>()
    .add(getOpenedCountSegment(engagement.openCount))
    .whenNotNil(lastViewedSegment)
    .whenNotNil(totalActiveTimeSegment)
    .whenNotNil(scrollSegment)
    .when(engagement.certificateClicked, i18n.t(ONLINE_OFFER_ENGAGEMENT_KEYS.certificateViewed))
    .join(ENGAGEMENT_SEGMENT_SEPARATOR);
};

const getOpenedCountSegment = (openCount: number) =>
  i18n.t(ONLINE_OFFER_ENGAGEMENT_KEYS.openedCount, {count: openCount});

const getLastViewedSegment = (lastOpenedAt: string | Nullish) => {
  const lastViewed = formatEngagementLastViewed(lastOpenedAt);

  if (isNil(lastViewed)) {
    return null;
  }

  return i18n.t(ONLINE_OFFER_ENGAGEMENT_KEYS.lastViewed, {time: lastViewed});
};

const getTotalActiveTimeSegment = (activeTimeMs: number) => {
  const totalActiveTime = formatEngagementActiveTime(activeTimeMs);

  if (isNil(totalActiveTime)) {
    return null;
  }

  return i18n.t(ONLINE_OFFER_ENGAGEMENT_KEYS.totalActiveTime, {duration: totalActiveTime});
};
